import { useState, useEffect, useRef } from "react";
import { BACKEND_API_URL } from "@/constants/api";
import { getIdToken } from "@/utils/authUtils";
import PracticeSessionStatsService from "@/services/PracticeSessionStatService";
import useStore from "@/store";

export const usePhonemeRecognition = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [phonemes, setPhonemes] = useState([]);
  const [overallAccuracy, setOverallAccuracy] = useState(0);
  const [adjustedWpm, setAdjustedWpm] = useState(0);
  const [fullRepeatCount, setFullRepeatCount] = useState(0);
  const [error, setError] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPredicting, setIsPredicting] = useState(false);
  const [directMatches, setDirectMatches] = useState([]);
  const [proficiency, setProficiency] = useState(null);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordedMimeTypeRef = useRef("");
  const abortProcessingRef = useRef(false);

  const { user } = useStore().user;
  const { settings } = useStore().settings;
  const { currentContent } = useStore().collections;

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const cancelPhonemeRecognition = () => {
    abortProcessingRef.current = true;
    setIsRecording(false);
    setPhonemes([]);
    setElapsedTime(0);
    setError("");
    recordedChunksRef.current = [];
    recordedMimeTypeRef.current = "";
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    }
  };

  // options.proficiency asks the backend for fluency / prosody / intelligibility
  // (slower, so only for full-sentence sessions).
  const startPhonemeRecognition = async (
    sentence,
    language,
    repeatable,
    save = true,
    options = {}
  ) => {
    abortProcessingRef.current = false;
    setIsRecording(true);
    setPhonemes([]);
    setError("");
    recordedChunksRef.current = [];
    recordedMimeTypeRef.current = "";

    let startTime;
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      let recorderOptions = undefined;
      if (typeof MediaRecorder !== "undefined" && typeof MediaRecorder.isTypeSupported === "function") {
        const preferredTypes = [
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/ogg;codecs=opus",
          "audio/ogg",
          "audio/mp4",
        ];
        for (const type of preferredTypes) {
          if (MediaRecorder.isTypeSupported(type)) {
            recorderOptions = { mimeType: type };
            break;
          }
        }
      }

      mediaRecorderRef.current = recorderOptions
        ? new MediaRecorder(streamRef.current, recorderOptions)
        : new MediaRecorder(streamRef.current);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
          if (!recordedMimeTypeRef.current && e.data.type) {
            recordedMimeTypeRef.current = e.data.type;
          }
        }
      };
      mediaRecorderRef.current.onstart = () => {
        startTime = Date.now();
      };

      mediaRecorderRef.current.onstop = async () => {
        const endTime = Date.now();
        setElapsedTime((endTime - startTime) / 1000);

        if (abortProcessingRef.current) {
          abortProcessingRef.current = false;
          recordedChunksRef.current = [];
          recordedMimeTypeRef.current = "";
          setIsRecording(false);
          return;
        }

        if (!recordedChunksRef.current.length) {
          recordedChunksRef.current = [];
          recordedMimeTypeRef.current = "";
          setIsRecording(false);
          return;
        }

        const inferredMimeType =
          recordedMimeTypeRef.current ||
          mediaRecorderRef.current?.mimeType ||
          (recordedChunksRef.current[0] ? recordedChunksRef.current[0].type : "") ||
          "audio/webm";
        const sanitizedMimeType = inferredMimeType.split(";")[0];
        const extension =
          sanitizedMimeType && sanitizedMimeType.includes("/")
            ? sanitizedMimeType.split("/")[1]
            : "webm";

        try {
          setIsPredicting(true);

          const audioBlob = new Blob(recordedChunksRef.current, { type: sanitizedMimeType });
          const formData = new FormData();
          formData.append("file", audioBlob, `recording.${extension}`);
          formData.append("source_text", sentence);
          formData.append("language", language);
          formData.append("repeatable", repeatable);
          formData.append("file_mime_type", sanitizedMimeType);
          formData.append("file_extension", extension);
          formData.append("proficiency", options.proficiency ? "true" : "false");
          // Easy = LangCraft lenient scoring, Hard = strict (no partial credit).
          formData.append("accuracy_mode", settings?.accuracy_mode || "easy");

          const token = await getIdToken();

          const response = await fetch(`${BACKEND_API_URL}/transcribe`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });

          if (!response.ok) {
            let details = "No details";
            try {
              const errJson = await response.json();
              details = errJson.details || errJson.error || JSON.stringify(errJson);
            } catch (e) {
              console.error("Failed to parse error JSON", e);
            }
            console.error("Transcribe API error:", response.status, details);
            throw new Error(`Transcribe failed: ${details}`);
          }

          const data = await response.json();
          setPhonemes(data.segments || []);
          setOverallAccuracy(data.overall_accuracy || 0);
          setFullRepeatCount(data.full_repeat_count || 0);
          const matches = Array.isArray(data.direct_matches)
            ? data.direct_matches
            : [];
          setDirectMatches(matches);
          setProficiency(data.proficiency || null);
          console.log("directMatches", data.direct_matches);

          const accuracyRate = data.overall_accuracy / 100;
          const rawWpm = data.segments.length / ((endTime - startTime) / 60000);
          setAdjustedWpm(rawWpm * accuracyRate);

          if (save) {
            PracticeSessionStatsService.createPracticeSessionStat(
              currentContent.id,
              user.current_collection_id,
              language,
              rawWpm * accuracyRate,
              data.overall_accuracy,
              data.segments.length,
              data.segments.map((p) => p.text),
              data.segments,
              [],
              (endTime - startTime) / 1000
            );
          }
        } catch (err) {
          console.error("Phoneme transcription failed:", err);
          const message =
            err instanceof Error && err.message ? err.message : "Transcribe failed";
          setError(message);
          setPhonemes([]);
          setOverallAccuracy(0);
          setAdjustedWpm(0);
          setFullRepeatCount(0);
          setDirectMatches([]);
          setProficiency(null);
        } finally {
          setIsPredicting(false);
          setIsRecording(false);
          recordedChunksRef.current = [];
          recordedMimeTypeRef.current = "";
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
          }
        }
      };

      mediaRecorderRef.current.start();
    } catch (err) {
      setError("Could not start recording: " + err.message);
      setIsRecording(false);
    }
  };

  const stopPhonemeRecognition = () => {
    setPhonemes([]);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    }
  };

  return {
    isRecording,
    phonemes,
    directMatches,
    proficiency,
    overallAccuracy,
    adjustedWpm,
    elapsedTime,
    fullRepeatCount,
    error,
    isPredicting,
    startPhonemeRecognition,
    stopPhonemeRecognition,
    cancelPhonemeRecognition,
  };
};
